import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEffectiveWeekStartDay } from "../../../hooks/useEffectiveWeekStartDay";
import { trackEvent } from "../../../utils/analytics";
import { getWeekStart } from "../../../utils/dateUtils";
import type { OpenShift, ShiftItem } from "../../../utils/models";
import { groupShiftsByWeek, weekKeyOf } from "./groupShiftsByWeek";
import { ShiftCard } from "./ShiftCard";

type ShiftWeekListProps = {
	shifts: ShiftItem[];
	selectedDate?: string;
	onDateSelect?: (date: string) => void;
	expandedShiftIds?: string[];
	onExpandOpenShift: (id: string) => void;
	allOpenShifts: Record<string, OpenShift[]>;
	staffScheduleDisplayUntilDate?: DateTime;
};

export const ShiftWeekList: FC<ShiftWeekListProps> = ({
	shifts,
	selectedDate,
	onDateSelect,
	expandedShiftIds = [],
	onExpandOpenShift,
	allOpenShifts,
	staffScheduleDisplayUntilDate,
}) => {
	const { t } = useTranslation();
	const weekStartDay = useEffectiveWeekStartDay();

	const handleItemClick = (dateKey: string): void => {
		onDateSelect?.(dateKey);

		if (expandedShiftIds.includes(dateKey)) {
			onExpandOpenShift("");

			trackEvent("shift_week_list_item_collapsed", {
				date: dateKey,
			});
		} else {
			onExpandOpenShift(dateKey);
		}
	};

	const groupedShifts = useMemo(() => groupShiftsByWeek(shifts, weekStartDay), [shifts, weekStartDay]);

	const getWeekShifts = useMemo(
		() => (dateKey: string) => {
			const weekKey = weekKeyOf(getWeekStart(DateTime.fromISO(dateKey), weekStartDay));
			const weekData = groupedShifts[weekKey];

			if (!weekData) return [];

			return Object.entries(weekData.shifts).flatMap(([date, shifts]) => {
				return shifts.map((shift) => ({
					date: DateTime.fromISO(date),
					shiftType: shift?.shiftType,
					hasDayRequest: shift?.hasDayRequest,
					hasNightRequest: shift?.hasNightRequest,
					departmentId: shift?.departmentId || "",
					isTimeOff: shift?.isTimeOff,
				}));
			});
		},
		[groupedShifts, weekStartDay],
	);

	const weekEntries = Object.entries(groupedShifts);
	const firstBlockedIndex = staffScheduleDisplayUntilDate
		? weekEntries.findIndex(([, { weekStart }]) => weekStart.startOf("day") > staffScheduleDisplayUntilDate.startOf("day"))
		: -1;
	const publishedEntries = firstBlockedIndex === -1 ? weekEntries : weekEntries.slice(0, firstBlockedIndex);

	return (
		<Box data-testid="my-schedule-shift-week-list" sx={{ width: "100%", borderRadius: "8px" }}>
			{publishedEntries.map(([weekKey, { weekStart, shifts: weekShifts, shiftCount }]) => (
				<Box key={weekKey} sx={{ mb: 4 }}>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 2,
							color: "text.secondary",
						}}
					>
						<Typography sx={(theme) => ({ color: theme.palette.primary.main, fontWeight: 550 })} variant="subtitle2">
							{weekKey}
						</Typography>
						<Typography sx={(theme) => ({ color: theme.palette.primary.main, fontWeight: 550 })} variant="subtitle2">
							{t("my.shifts")} ({shiftCount})
						</Typography>
					</Box>

					{Array.from({ length: 7 }).map((_, index) => {
						const currentDate = weekStart.plus({ days: index });
						const dateKey = currentDate.toFormat("yyyy-MM-dd");
						const shifts = weekShifts[dateKey];
						const isSelected = dateKey === selectedDate;

						return (
							<ShiftCard
								key={dateKey}
								dateKey={dateKey}
								currentDate={currentDate}
								shifts={shifts}
								isSelected={isSelected}
								isExpanded={expandedShiftIds.includes(dateKey)}
								onItemClick={() => handleItemClick(dateKey)}
								weekShifts={getWeekShifts(dateKey)}
							/>
						);
					})}
				</Box>
			))}
		</Box>
	);
};
